import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User as FirebaseUser,
  sendPasswordResetEmail,
  updateProfile as updateFirebaseProfile,
} from 'firebase/auth';
import { createContext, ReactNode, useContext, useEffect, useState, useMemo } from 'react';

import { auth, googleProvider } from '../config/firebase';
import { createUser, getUser, updateUser } from '../services/users';
import { AuthContextType, AuthError, User } from '../types/auth';

// Bandera para activar el modo de desarrollo sin Firebase real
const USE_MOCK_AUTH = process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCK_AUTH === 'true';

// Crear el contexto de autenticación
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Props para el proveedor de autenticación
interface AuthProviderProps {
  children: ReactNode;
}

// Convertir un usuario de Firebase a nuestro tipo User
const formatUser = async (user: FirebaseUser): Promise<User> => {
  try {
    // Intentar obtener datos del usuario desde Firestore
    const userProfile = await getUser(user.uid);
    
    if (userProfile) {
      // Usuario existe en Firestore
      return {
        uid: userProfile.uid,
        email: userProfile.email,
        displayName: userProfile.displayName,
        photoURL: userProfile.photoURL,
        role: userProfile.role,
        createdAt: userProfile.createdAt instanceof Date ? userProfile.createdAt : userProfile.createdAt.toDate(),
        lastLogin: new Date(),
      };
    } else {
      // Usuario no existe en Firestore, crear nuevo registro
      const newUser: User = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        role: 'user',
        createdAt: new Date(),
        lastLogin: new Date(),
      };
      
      // Crear usuario en Firestore
      await createUser(user.uid, {
        email: user.email || '',
        displayName: user.displayName || '',
        photoURL: user.photoURL,
      });
      
      return newUser;
    }
  } catch (error) {
    console.error('Error obteniendo datos de usuario:', error);
    
    // Fallback: crear usuario básico
    return {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      role: 'user',
      createdAt: new Date(),
      lastLogin: new Date(),
    };
  }
};

// Proveedor de autenticación
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);

  // Mock de usuario para desarrollo
  const mockUser: User = useMemo(() => ({
    uid: 'mock-uid-12345',
    email: 'usuario@ejemplo.com',
    displayName: 'Usuario de Prueba',
    photoURL: null,
    createdAt: new Date(),
    lastLogin: new Date(),
    role: 'user',
  }), []);

  // Manejar errores de Firebase
  const handleError = (error: unknown): AuthError => {
    console.error('Auth error:', error);
    const err = error as Error & { code?: string };
    return {
      code: err.code || 'auth/unknown',
      message: err.message || 'Ha ocurrido un error desconocido',
    };
  };

  // Efecto para escuchar cambios en el estado de autenticación
  useEffect(() => {
    // Si estamos en modo de desarrollo con autenticación simulada
    if (USE_MOCK_AUTH) {
      console.log('🔧 Usando autenticación simulada para desarrollo');
      const timer = setTimeout(() => {
        setUser(mockUser);
        setLoading(false);
      }, 1000);

      return () => clearTimeout(timer);
    }
    
    // Autenticación real con Firebase
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          const formattedUser = await formatUser(firebaseUser);
          setUser(formattedUser);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error en onAuthStateChanged:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    });

    // Limpieza al desmontar
    return () => unsubscribe();
  }, [mockUser]);

  // Iniciar sesión con correo y contraseña
  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      
      if (USE_MOCK_AUTH) {
        console.log('🔧 Login simulado con:', email);
        await new Promise(resolve => setTimeout(resolve, 1000));
        setUser(mockUser);
        return;
      }
      
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError(handleError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Iniciar sesión con Google
  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (USE_MOCK_AUTH) {
        console.log('🔧 Login con Google simulado');
        await new Promise(resolve => setTimeout(resolve, 1000));
        setUser({
          ...mockUser,
          displayName: 'Usuario Google',
          photoURL: 'https://via.placeholder.com/150',
        });
        return;
      }
      
      await signInWithPopup(auth, googleProvider);
      
    } catch (err) {
      setError(handleError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Registrar nuevo usuario
  const register = async (email: string, password: string, displayName: string) => {
    try {
      setLoading(true);
      setError(null);
      
      if (USE_MOCK_AUTH) {
        console.log('🔧 Registro simulado:', { email, displayName });
        await new Promise(resolve => setTimeout(resolve, 1000));
        setUser({
          ...mockUser,
          email,
          displayName,
        });
        return;
      }
      
      // Crear usuario en Firebase Auth
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Actualizar perfil con displayName
      await updateFirebaseProfile(result.user, { displayName });
      
    } catch (err) {
      setError(handleError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Cerrar sesión
  const logout = async () => {
    try {
      setError(null);
      
      if (USE_MOCK_AUTH) {
        console.log('🔧 Logout simulado');
        setUser(null);
        return;
      }
      
      await signOut(auth);
    } catch (err) {
      setError(handleError(err));
      throw err;
    }
  };

  // Restablecer contraseña
  const resetPassword = async (email: string) => {
    if (USE_MOCK_AUTH) {
      console.log('🔧 Restablecimiento de contraseña simulado para:', email);
      await new Promise(resolve => setTimeout(resolve, 1000));
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError(handleError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Actualizar perfil
  const updateProfile = async (profile: Partial<User>) => {
    try {
      if (!user) throw new Error('No hay usuario autenticado');
      
      setLoading(true);
      setError(null);
      
      // Convertir tipos Date a los esperados por Firestore (si es necesario)
      const firestoreProfile = {
        ...profile,
        // Remover campos que no deben actualizarse directamente
        uid: undefined,
        createdAt: undefined,
        lastLogin: undefined, // Se actualiza automáticamente en el servicio
      };
      
      // Actualizar en Firestore usando el servicio
      await updateUser(user.uid, firestoreProfile);
      
      // Actualizar estado local
      setUser(prevUser => prevUser ? { ...prevUser, ...profile } : null);
    } catch (err) {
      setError(handleError(err));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Valor del contexto
  const value = {
    user,
    loading,
    error,
    login,
    loginWithGoogle,
    register,
    logout,
    resetPassword,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook personalizado para usar el contexto de autenticación
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};
